import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import QueryProvider from './app/providers/QueryProvider';
import ProtectedRoute from './components/common/ProtectedRoute';
import Navigation from './components/layout/Navigation';
import TopHeader from './components/layout/TopHeader';
import DataFreshnessBanner from './components/layout/DataFreshnessBanner';
import ThemeEditor from './components/layout/ThemeEditor';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import SessionExpiredModal from './components/common/SessionExpiredModal';
import { LoadingSpinner } from './components/common/ui';
import { useUIStore } from './stores/uiStore';

import SideDock from './components/layout/SideDock';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const RelationsPage = lazy(() => import('./pages/RelationsPage'));
const GraphVisualization = lazy(() => import('./pages/GraphVisualization'));
const BidderDetail = lazy(() => import('./pages/BidderDetail'));
const TenderDetail = lazy(() => import('./pages/TenderDetail'));
const OrganizationDetail = lazy(() => import('./pages/OrganizationDetail'));
const LegalEntityDetail = lazy(() => import('./pages/LegalEntityDetail'));
const GovernorDetail = lazy(() => import('./pages/GovernorDetail'));
const PersonDetail = lazy(() => import('./pages/PersonDetail'));
const PipelinePage = lazy(() => import('./pages/PipelinePage'));
const CorruptionPersonDetail = lazy(() => import('./pages/CorruptionPersonDetail'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const NotFound = lazy(() => import('./components/common/NotFound'));
const TenderComparePage = lazy(() => import('./pages/TenderComparePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const WorkspacePage = lazy(() => import('./pages/WorkspacePage'));
const DashboardListPage = lazy(() => import('./pages/DashboardListPage'));
const DashboardDetailPage = lazy(() => import('./pages/DashboardDetailPage'));
const FraudDetection = lazy(() => import('./pages/FraudDetection'));
const AiAssistantPage = lazy(() => import('./pages/AiAssistantPage'));

const AppLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPage = location.pathname === '/login';
  const isHomePage = location.pathname === '/';
  const { toggleTheme, fullscreen, setFullscreen } = useUIStore();

  // Exit fullscreen on route change or Esc key
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fullscreen, setFullscreen]);

  useEffect(() => {
    if (fullscreen) setFullscreen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (isLoginPage) return;
    let gPressedAt = 0;

    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable;

      // Ctrl+K: navigate to search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        navigate('/workspace/search');
        return;
      }

      if (isInput) return;

      // T: toggle theme
      if (e.key === 't' || e.key === 'T') { toggleTheme(); return; }

      // G+X two-key nav shortcuts
      const now = Date.now();
      if (e.key === 'g' || e.key === 'G') { gPressedAt = now; return; }
      if (now - gPressedAt < 800) {
        if (e.key === 'd') { navigate('/dashboard'); gPressedAt = 0; return; }
        if (e.key === 'g') { navigate('/graph'); gPressedAt = 0; return; }
        if (e.key === 'c') { navigate('/ai-assistant'); gPressedAt = 0; return; }
        if (e.key === 's') { navigate('/workspace/search'); gPressedAt = 0; return; }
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isLoginPage, navigate, toggleTheme]);

  const showChrome = !isLoginPage && !fullscreen;
  const showAiPanel = showChrome && !isHomePage;
  // AI туслах дээр чат нь хуудасны тал хэсэг — самбарыг өргөн болгоно
  const aiPage = location.pathname.startsWith('/ai-assistant');
  return (
    <div className={`app${fullscreen ? ' app--fullscreen' : ''}`}>
      {!isLoginPage && <div className="app-bg-layer" />}
      {showChrome && <Navigation />}
      {showChrome && <TopHeader />}
      {showChrome && <ThemeEditor />}
      {!isLoginPage && <SessionExpiredModal />}
      <main className={isLoginPage ? '' : 'main-content'}>
        {/* the home page shows the dates in its own card */}
        {showChrome && !isHomePage && <DataFreshnessBanner />}
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<Navigate to="/" replace />} />
            <Route path="/login" element={<LoginPage />} />

            {/* All authenticated users */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/relations" element={<ProtectedRoute><RelationsPage /></ProtectedRoute>} />
            <Route path="/relations/:view" element={<ProtectedRoute><RelationsPage /></ProtectedRoute>} />
            <Route path="/relations/:view/:graphId" element={<ProtectedRoute><RelationsPage /></ProtectedRoute>} />
            {/* Search is now a mode within Workspace — keep the old URL alive for bookmarks/links */}
            <Route path="/search" element={<Navigate to="/workspace/search" replace />} />
            <Route path="/bidder/:id" element={<ProtectedRoute><BidderDetail /></ProtectedRoute>} />
            <Route path="/tender/:id" element={<ProtectedRoute><TenderDetail /></ProtectedRoute>} />
            <Route path="/organization/:id" element={<ProtectedRoute><OrganizationDetail /></ProtectedRoute>} />
            <Route path="/legal-entity/:register" element={<ProtectedRoute><LegalEntityDetail /></ProtectedRoute>} />
            <Route path="/governor/:id" element={<ProtectedRoute><GovernorDetail /></ProtectedRoute>} />
            <Route path="/person/:id" element={<ProtectedRoute><PersonDetail /></ProtectedRoute>} />

            {/* VIP + Admin */}
            <Route path="/graph" element={<ProtectedRoute allowedRoles={['vip', 'admin']}><GraphVisualization /></ProtectedRoute>} />
            <Route path="/fraud" element={<ProtectedRoute allowedRoles={['vip', 'admin']}><FraudDetection /></ProtectedRoute>} />
            <Route path="/corruption-persons" element={<Navigate to="/fraud" replace />} />
            <Route path="/corruption-persons/:id" element={<ProtectedRoute allowedRoles={['admin']}><CorruptionPersonDetail /></ProtectedRoute>} />
            <Route path="/direct-purchases" element={<Navigate to="/workspace" replace />} />

            {/* Workspace: Search + Chart builder + Dashboard editor + Notebook + AI */}
            {/* «Ажлын орчин» hub цэснээс нуугдсан — хуучин холбоос AI туслах руу. Builder/тайлангийн
                дэд замууд кодонд үлдсэн (шууд холбоосоор нээгдэнэ). */}
            <Route path="/workspace" element={<Navigate to="/ai-assistant" replace />} />
            <Route path="/workspace/search" element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>} />
            <Route path="/workspace/builder" element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>} />
            <Route path="/workspace/builder/:queryId" element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>} />
            <Route path="/workspace/reports" element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>} />
            <Route path="/workspace/dashboard" element={<ProtectedRoute><DashboardListPage /></ProtectedRoute>} />
            <Route path="/workspace/dashboard/:id" element={<ProtectedRoute><DashboardDetailPage /></ProtectedRoute>} />

            {/* Chat, Documents, Profile */}
            <Route path="/ai-assistant" element={<ProtectedRoute><AiAssistantPage /></ProtectedRoute>} />
            <Route path="/ai-assistant/builder" element={<Navigate to="/ai-assistant" replace />} />
            <Route path="/ai-assistant/builder/:queryId" element={<ProtectedRoute><AiAssistantPage /></ProtectedRoute>} />
            <Route path="/chat" element={<Navigate to="/ai-assistant" replace />} />
            <Route path="/documents" element={<Navigate to="/" replace />} />
            <Route path="/profile" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
            <Route path="/compare" element={<ProtectedRoute><TenderComparePage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/help" element={<HelpPage />} />

            {/* Admin only */}
            <Route path="/pipeline" element={<ProtectedRoute allowedRoles={['admin']}><PipelinePage /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute allowedRoles={['admin']}><UsersPage /></ProtectedRoute>} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {/* the right side: the AI assistant and the open page's tools, one column */}
      {showAiPanel && <SideDock wide={aiPage} />}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <AppLayout />
          </AuthProvider>
        </BrowserRouter>
      </QueryProvider>
    </ErrorBoundary>
  );
};

export default App;
